import { makeStyles } from '@material-ui/core'
import { MessageOutlined as MessageIcon } from '@material-ui/icons'
import { unwrapResult } from '@reduxjs/toolkit'
import PropTypes from 'prop-types'
import React from 'react'
import { useHistory } from 'react-router-dom'

import { useActionRunner } from 'src/modules/notification'
import { Button, FormikDialog, Order, Typography } from 'src/modules/ui'

import MessageSupplierModal from '../../messages/components/MessageSupplierModal'
import { usePurchasing } from '../hooks'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 2, 4),
    [theme.breakpoints.up('mobile')]: {
      paddingTop: theme.spacing(4),
    },
  },
  actionButton: {
    marginBottom: theme.spacing(2),
  },
  unavailable: {
    marginTop: theme.spacing(2),
    maxWidth: 400,
  },
  unavailableTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
}))

const SubmittedOrder = ({ id, lineItems, supplier, ...order }) => {
  const classes = useStyles()
  const {
    createDraftOrder,
    submitOrderReceivedOk,
    submitOrderReceivedOkAfterDispute,
    submitDisputeWithOrder,
    purchasingEnvironment,
    editDisputeReasonForLineItem,
    submitDisputeResolvedForOrder,
  } = usePurchasing()
  const [doDraftOrder] = useActionRunner(createDraftOrder)
  const [doSubmitOrderReceivedOk] = useActionRunner(submitOrderReceivedOk)
  const [doSubmitOrderReceivedOkAfterDispute] = useActionRunner(
    submitOrderReceivedOkAfterDispute
  )
  const [doSubmitDisputeWithOrder] = useActionRunner(submitDisputeWithOrder)
  const [doEditDisputeReasonForLineItem] = useActionRunner(
    editDisputeReasonForLineItem
  )
  const [doSubmitDisputeResolvedForOrder] = useActionRunner(
    submitDisputeResolvedForOrder
  )

  const [showDisputeItems, setShowDisputeItems] = React.useState(false)

  const history = useHistory()

  const handleDraftReorder = async () => {
    const action = await doDraftOrder({
      environmentId: purchasingEnvironment,
      lineItems: lineItems
        .filter(item => item.agreementProduct)
        .map(item => ({
          agreementProductId: item.agreementProduct.id,
          quantity: item.quantity,
        })),
    })
    try {
      const payload = unwrapResult(action)
      history.push(`/purchasing/drafts/${payload.id}`)
    } catch (error) {}
  }

  const handleAllReceivedOk = async () => {
    try {
      await doSubmitOrderReceivedOk(id)
      history.push(`/purchasing/submitted`)
    } catch (error) {}
  }

  const handleAllReceivedOkAfterDispute = async () => {
    try {
      await doSubmitOrderReceivedOkAfterDispute(id)
      history.push(`/purchasing/submitted`)
    } catch (error) {}
  }

  const handleRaiseDisputeWithOrder = async () => {
    setShowDisputeItems(true)
  }

  const handleSubmitDisputeWithOrder = async () => {
    console.log('>>>', disputeNotes)

    try {
      await Promise.all(
        lineItems.map(async lineItem => {
          console.log('>>> lineItem', lineItem)
          return await doEditDisputeReasonForLineItem({
            id: lineItem.id,
            data: {
              ...lineItem,
              disputeReason: disputeNotes[lineItem.agreementProduct.product.id],
            },
          })
        }),
        await doSubmitDisputeWithOrder(id)
      )
      history.push(`/purchasing/submitted`)
    } catch (error) {}
  }

  const handleUndisputeOrder = async () => {
    try {
      await doSubmitDisputeResolvedForOrder(id)
      history.push(`/purchasing/submitted`)
    } catch (error) {}
  }

  const orderInOkState = order =>
    order.status === 'dispatched' ||
    order.status === 'submitted' ||
    order.status === 'updated_by_supplier'

  const unavailable = lineItems.filter(lineItem => !lineItem.agreementProduct)
  const disputeNotes = {}
  lineItems.forEach(lineItem => {
    if (lineItem.agreementProduct) {
      disputeNotes[lineItem.agreementProduct.product.id] =
        lineItem.disputeReason
    }
  })

  return (
    <Order
      {...order}
      id={id}
      lineItems={lineItems}
      organisation={supplier}
      showDisputed={!!showDisputeItems}
      disputeNotes={disputeNotes}
      sidePanelItems={
        <>
          {orderInOkState(order) ? (
            <FormikDialog
              onSubmit={handleAllReceivedOk}
              submitText="All Received OK"
              trigger={({ onClick }) => (
                <Button
                  className={classes.actionButton}
                  color="lightGreen"
                  fullWidth
                  onClick={onClick}
                >
                  All Received OK
                </Button>
              )}
            >
              <Typography>Have all your items been received?</Typography>
            </FormikDialog>
          ) : null}
          {order.status === 'issue_raised' ? (
            <FormikDialog
              onSubmit={handleAllReceivedOkAfterDispute}
              submitText="All Received OK"
              trigger={({ onClick }) => (
                <Button
                  className={classes.actionButton}
                  color="lightGreen"
                  fullWidth
                  onClick={onClick}
                >
                  All Received OK
                </Button>
              )}
            >
              <Typography>Have all your items been received?</Typography>
            </FormikDialog>
          ) : null}
          <FormikDialog
            onSubmit={handleDraftReorder}
            submitText="Draft Order Again"
            trigger={({ onClick }) => (
              <Button
                className={classes.actionButton}
                color="lightGreen"
                fullWidth
                onClick={onClick}
              >
                Draft Order Again
              </Button>
            )}
          >
            <Typography>Create new draft from this order?</Typography>
            {unavailable.length > 0 && (
              <div className={classes.unavailable}>
                <Typography className={classes.unavailableTitle}>
                  Note that the following products are not available any more so
                  will not be reordered:
                </Typography>
                {unavailable.map(({ productName }, idx) => (
                  <Typography key={idx}>{productName}</Typography>
                ))}
              </div>
            )}
          </FormikDialog>

          {!showDisputeItems && orderInOkState(order) ? (
            <Button
              className={classes.actionButton}
              color="orange"
              fullWidth
              onClick={handleRaiseDisputeWithOrder}
            >
              Raise Issue with Order
            </Button>
          ) : null}

          {orderInOkState(order) && showDisputeItems ? (
            <FormikDialog
              onSubmit={handleSubmitDisputeWithOrder}
              submitText="Submit Issue with Order"
              trigger={({ onClick }) => (
                <Button
                  className={classes.actionButton}
                  color="orange"
                  fullWidth
                  onClick={onClick}
                >
                  Submit Issue with Order
                </Button>
              )}
            >
              <Typography>
                Do you want to raise an issue with this order?
              </Typography>
            </FormikDialog>
          ) : null}

          {order.status === 'issue_raised' && (
            <FormikDialog
              onSubmit={handleUndisputeOrder}
              submitText="Issue Resolved"
              trigger={({ onClick }) => (
                <Button
                  className={classes.actionButton}
                  color="orange"
                  fullWidth
                  onClick={onClick}
                >
                  Issue Resolved
                </Button>
              )}
            >
              <Typography>
                Do to you want to mark this issue as resolved?
              </Typography>
            </FormikDialog>
          )}
          <MessageSupplierModal
            environmentId={purchasingEnvironment}
            subject={`Re: Order ${id}`}
            supplier={supplier}
            trigger={props => (
              <Button
                color="white"
                fullWidth
                startIcon={<MessageIcon />}
                {...props}
              >
                Contact Supplier
              </Button>
            )}
          />
        </>
      }
    />
  )
}

SubmittedOrder.propTypes = {
  lineItems: PropTypes.array,
  supplier: PropTypes.object,
  disputeReasons: PropTypes.array,
}

export default SubmittedOrder
